import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../common/SEO';
import Layout from '../common/Layout';
import Button from '../components/ui/Button';
import { Eyebrow, SectionHead } from '../components/ui/SectionHead';
import Notice from '../components/ui/Notice';
import Card from '../components/ui/Card';
import DlFacts from '../components/ui/DlFacts';
import { entity } from '../content/en';
import { organizationSchema, webSiteSchema, breadcrumbSchema } from '../components/ui/schema';

const Home = () => {
  const { t } = useTranslation('cy');

  const schema = [
    organizationSchema(),
    webSiteSchema(),
    breadcrumbSchema([{ label: 'Home', to: '/' }]),
  ];

  return (
    <Layout>
      <SEO title={t('home.meta.title')} description={t('home.meta.description')} path="/" schema={schema} />

      {/* home-hero */}
      <section className="cy-hero" id="home-hero">
        <div className="cy-hero__arc" aria-hidden="true" />
        {/* Content block nests inside .cy-container so it left-aligns to the same
            gutter as the sections below (origin/main ceae8c6), while keeping the
            data-led kicker and the boundary next to the claim it qualifies. */}
        <div className="cy-container">
          <div className="cy-hero__inner">
            <Eyebrow>{t('home.hero.eyebrow')}</Eyebrow>
            <h1 className="cy-hero__title">{t('home.hero.h1')}</h1>
            <p className="cy-hero__lead">{t('home.hero.lead')}</p>
            <p className="cy-kicker">{t('home.hero.kicker')}</p>
            <Notice variant="inline" className="cy-hero__boundary">
              {t('home.hero.boundary')}
            </Notice>
            <div className="cy-hero__cta">
              <a className="cy-btn cy-btn--primary" href="#home-pillars">
                {t('cta.explore')}
              </a>
              <Link className="cy-link" to="/submit-opportunity">
                {t('cta.submit')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* home-pillars */}
      <section className="cy-section cy-section--alt" id="home-pillars">
        <div className="cy-container">
          <SectionHead eyebrow={t('home.pillars.eyebrow')} title={t('home.pillars.h2')} lead={t('home.pillars.lead')} />
          <div className="cy-grid cy-grid--3">
            {t('home.pillars.items', { returnObjects: true }).map((item) => (
              /* The self-identification question sits directly under the title,
                 ahead of the descriptor: the home page routes by audience before
                 it describes the pillar (06 §1.2 P1). */
              <Card key={item.title} to={item.to} title={item.title} cta={item.cta}>
                <p className="cy-card-question">{item.question}</p>
                <p className="cy-card-text">{item.desc}</p>
              </Card>
            ))}
          </div>
          <p className="cy-fineprint" style={{ marginTop: 32 }}>
            {t('home.pillars.boundary')}
          </p>
        </div>
      </section>

      {/* home-selection-principle */}
      <section className="cy-section cy-zone--doc" id="home-selection-principle">
        <div className="cy-container cy-split">
          <div className="cy-split__main">
            <SectionHead eyebrow={t('home.stance.eyebrow')} title={t('home.stance.h2')} lead={t('home.stance.lead')} />
            <p style={{ marginTop: 24 }}>
              <Link className="cy-link" to="/approach#approach-gates">
                {t('home.stance.cta')}
              </Link>
            </p>
          </div>
          <div className="cy-split__aside">
            {t('home.stance.principles', { returnObjects: true }).map((p, i) => (
              <div className="cy-principle" key={i}>
                <h3 className="cy-principle__title">{p.title}</h3>
                <p className="cy-card-text">{p.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Discipline disclosure promoted to home-section level (04 map, tier B/D HIGH;
            05 contract, pillar 1). Content is existing site material: the gate stop
            signals, the stage stop conditions and the per-capability forbidden claims. */}
        <div className="cy-container" style={{ marginTop: 64 }}>
          <div className="cy-stops">
            <h3 className="cy-stops__title">{t('home.stance.stops.h3')}</h3>
            <p className="cy-stops__lead">{t('home.stance.stops.lead')}</p>
            <ul className="cy-stops__list">
              {t('home.stance.stops.items', { returnObjects: true }).map((item) => (
                <li className="cy-stops__item" key={item.title}>
                  <h4 className="cy-stops__item-title">{item.title}</h4>
                  <p className="cy-stops__item-body">{item.body}</p>
                </li>
              ))}
            </ul>
            <p className="cy-fineprint" style={{ marginTop: 28 }}>
              {t('home.stance.stops.note')}{' '}
              <Link className="cy-link" to="/property-development#property-not-fit">
                {t('home.stance.stops.notFitLink')}
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* home-value-role */}
      <section className="cy-section cy-section--alt" id="home-value-role">
        <div className="cy-container">
          <SectionHead title={t('home.role.h2')} lead={t('home.role.lead')} />
          <div className="cy-grid cy-grid--3">
            {t('home.role.groups', { returnObjects: true }).map((g) => (
              <div className="cy-card" key={g.title}>
                <h3 className="cy-card-title">{g.title}</h3>
                <p className="cy-card-text">{g.body}</p>
              </div>
            ))}
          </div>
          <p className="cy-fineprint" style={{ marginTop: 32 }}>{t('home.role.note')}</p>
          <p style={{ marginTop: 16 }}>
            <Link className="cy-link" to="/approach#approach-stages">
              {t('home.role.cta')}
            </Link>
          </p>
        </div>
      </section>

      {/* home-connection */}
      <section className="cy-section" id="home-connection">
        <div className="cy-container">
          <SectionHead eyebrow={t('home.connection.eyebrow')} title={t('home.connection.h2')} lead={t('home.connection.lead')} />
          <div className="cy-grid cy-grid--3">
            {t('home.connection.rows', { returnObjects: true }).map((r) => (
              <div className="cy-card" key={r.pair}>
                <h3 className="cy-card-title">{r.pair}</h3>
                <p className="cy-card-text">{r.link}</p>
              </div>
            ))}
          </div>
          <Notice variant="compliance" className="cy-notice--inline" style={{ marginTop: 32 }}>
            {t('home.connection.boundary')}
          </Notice>
          <p style={{ marginTop: 16 }}>
            <Link className="cy-link" to="/approach#approach-by-pillar">
              {t('home.connection.cta')}
            </Link>
          </p>
        </div>
      </section>

      {/* home-local-proof */}
      <section className="cy-section cy-section--alt" id="home-local-proof">
        <div className="cy-container cy-container--reading">
          <SectionHead eyebrow={t('home.local.eyebrow')} title={t('home.local.h2')} />
          <DlFacts
            rows={[
              { label: 'Entity', value: entity.name },
              { label: 'Address', value: entity.address },
              { label: 'Phone', value: entity.phone, href: entity.phoneHref },
              { label: 'Email', value: entity.email, href: entity.emailHref },
            ]}
          />
          <p className="cy-fineprint" style={{ marginTop: 24 }}>
            {t('home.local.boundary')}{' '}
            <Link className="cy-link" to="/about#about-entity">
              {t('home.local.aboutLink')}
            </Link>
          </p>
        </div>
      </section>

      {/* home-approach-preview */}
      <section className="cy-section" id="home-approach-preview">
        <div className="cy-container">
          <SectionHead eyebrow={t('home.preview.eyebrow')} title={t('home.preview.h2')} />
          <p className="cy-lede" style={{ marginTop: 0, marginBottom: 40 }}>{t('home.preview.body')}</p>
          <ol className="cy-stage-row" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: 32 }}>
            {t('home.preview.stages', { returnObjects: true }).map((name, i) => (
              <li key={name} style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                <span className="cy-stage__num">{String(i + 1).padStart(2, '0')}</span>
                <span style={{ fontWeight: 600, color: 'var(--cy-color-text-primary)' }}>{name}</span>
              </li>
            ))}
          </ol>
          <p className="cy-fineprint" style={{ marginTop: 24 }}>{t('home.preview.note')}</p>
          <p className="cy-body" style={{ marginTop: 24 }}>
            {t('home.preview.lens')}{' '}
            <Link className="cy-link" to="/approach#approach-gates">{t('home.preview.lensGate')}</Link>
            {' · '}
            <Link className="cy-link" to="/property-development#property-assessment">{t('home.preview.lensProperty')}</Link>
          </p>
          <p style={{ marginTop: 24 }}>
            <Link className="cy-link" to="/approach#approach-stages">
              {t('home.preview.cta')}
            </Link>
          </p>
        </div>
      </section>

      {/* home-final-cta */}
      <section className="cy-section cy-section--cta" id="home-final-cta">
        <div className="cy-container cy-cta-band">
          <h2 className="cy-title" style={{ textAlign: 'center' }}>{t('home.finalCta.h2')}</h2>
          <p className="cy-lede" style={{ margin: '16px auto 0', textAlign: 'center' }}>{t('home.finalCta.body')}</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center', marginTop: 32 }}>
            <Button to="/submit-opportunity">{t('cta.submit')}</Button>
            <Button to="/approach" variant="secondary">{t('home.finalCta.review')}</Button>
          </div>
          <p style={{ marginTop: 16, textAlign: 'center' }}>
            <Link className="cy-link" to="/contact">{t('home.finalCta.contact')}</Link>
          </p>
          <p className="cy-fineprint" style={{ marginTop: 24, textAlign: 'center' }}>
            {t('home.finalCta.privacy')}{' '}
            <Link className="cy-link" to="/privacy">Privacy Policy</Link>
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
